import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { isValidId } from '../u2000Core/tools/U2000IdUtils';
import { NS } from './U3121';
import {
  doGetLandRegisterCoverage,
  doGetSubProjectLandRegisterCoverage,
  luHandleAppiError,
  type U3121LandRegisterCoverage,
} from './U3121Service';

function parseCoverageResult(raw: unknown): U3121LandRegisterCoverage | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const lotsRaw = Array.isArray(r.lots) ? r.lots : [];
  const lots = lotsRaw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const lotEntry = entry as Record<string, unknown>;
      const lot = String(lotEntry.lot ?? '').trim();
      if (!lot) return null;
      return {
        lot,
        existsInRegister: Boolean(lotEntry.existsInRegister),
      };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  return {
    projectNumber: String(r.projectNumber ?? ''),
    projectDescription: String(r.projectDescription ?? ''),
    lots,
  };
}

/** Couverture registre foncier projet / sous-projet (GET landRegisterCoverage, BL-0035). */
export function useU3121LandRegisterCoverageHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const handleError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      errorResult.genericErrors?.forEach((err) => toast.error(err.text, err.title));
    },
    [t, toast],
  );

  const getCoverage = useCallback(
    async (projectId: string): Promise<U3121LandRegisterCoverage | null> => {
      const pid = projectId?.trim();
      if (!pid || !isValidId(pid)) return null;
      try {
        const response = await doGetLandRegisterCoverage(axios, pid);
        return parseCoverageResult(response.data?.result);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  const getSubProjectCoverage = useCallback(
    async (projectId: string, subProjectId: string): Promise<U3121LandRegisterCoverage | null> => {
      const pid = projectId?.trim();
      const spid = subProjectId?.trim();
      if (!pid || !isValidId(pid) || !spid || !isValidId(spid)) return null;
      try {
        const response = await doGetSubProjectLandRegisterCoverage(axios, pid, spid);
        return parseCoverageResult(response.data?.result);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  return { getCoverage, getSubProjectCoverage };
}
